import React, { useEffect, useState, useRef } from "react";
import { Box, Button, Grid, Modal, Theme, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { splitTextByWord } from "../Utils/Utilities";

export interface GenericModalData {
  title: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText?: string;
  boldText?: string;
  theme: Theme;
  verticalPositionForMobile?: string;
  alertBoxStyle?: {
    distributionDirection?: string;
    contentJustifyType: string;
    alignmentType: string;
    spacing: number;
    primaryActionButtonStyle: {
      backgroundColor: string;
      borderColor: string;
      color: string;
      hoverBackgroundColor: string;
      hoverTextColor: string;
      width: string;
      buttonOrder?: number;
    };
    secondaryActionButtonStyle?: {
      color: string;
    };
  };
  closeButtonActionHandler: () => void;
  primaryButtonActionHandler: () => void;
  secondaryButtonActionHandler?: () => void;
}

export default function GenericModal(props: GenericModalData) {
  const isMobile = useMediaQuery(props.theme.breakpoints.down("sm"));
  const [modalTop, setModalTop] = useState(props.verticalPositionForMobile);
  const modalRef = useRef<HTMLDivElement>(null);
  const text = props.description;
  const boldWord = props.boldText ? props.boldText : "";
  const parts = boldWord ? splitTextByWord(text, boldWord) : [];
  const secondaryActionButtonStyle = Object.assign(
    {},
    props.theme.custom.styles.genericTertiaryButton,
    props.alertBoxStyle?.secondaryActionButtonStyle
  );

  useEffect(() => {
    if (modalRef.current) {
      const modalHeight = modalRef.current.offsetHeight;
      const windowHeight = window.innerHeight;
      const calculatedTop = Math.max((windowHeight - modalHeight) / 2, 0);
      setModalTop(`${calculatedTop}px`);
    }
  }, [isMobile]);

  return (
    <Modal open={true} onClose={props.closeButtonActionHandler}>
      <Box
        ref={modalRef}
        sx={{
          position: "absolute",
          boxShadow: props.theme.shadows[5],
          left: "50%",
          top: modalTop,
          transform: "translate(-50%, 0)",
          backgroundColor: props.theme.custom.colors.bodyColor,
          borderRadius: props.theme.custom.styles.borderRadius.radiusModal,
          width: isMobile ? "90%" : "488px",
          minWidth: "220px",
          outline: "none",
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid container item>
            <Grid item xs={10}>
              <Typography
                sx={{
                  ...props.theme.custom.styles.modalHeader,
                  padding: "1rem 1rem .5rem 1rem",
                }}
              >
                {props.title}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{ display: "flex", justifyContent: "flex-end" }}
              xs={2}
            >
              <Button
                startIcon={<CloseIcon />}
                onClick={props.closeButtonActionHandler}
                sx={{
                  minWidth: "auto",
                  px: "1rem",
                  py: "1rem",
                  color: props.theme.custom.styles.modalHeader.closeIconColor,
                  alignItems: "baseline",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {props.theme.custom.styles.underLineModalHeader && (
                <hr style={props.theme.custom.styles.modalHeaderHrStyle}></hr>
              )}
            </Grid>
            <Grid container>
              <Grid style={{ padding: ".5rem 1rem .5rem 1rem" }} item xs={12}>
                {props.boldText ? (
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {parts.map((part, index) =>
                      part.toLowerCase() === boldWord.toLowerCase() ? (
                        <Typography
                          component="span"
                          sx={{ fontWeight: "bold" }}
                          key={index}
                        >
                          {part}
                        </Typography>
                      ) : (
                        part
                      )
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body1"> {props.description} </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                {props.theme.custom.styles.underLineModalDescription && (
                  <hr style={props.theme.custom.styles.modalHeaderHrStyle}></hr>
                )}
              </Grid>
              <Grid
                item
                container
                sx={{ m: "1rem" }}
                justifyContent={props.alertBoxStyle?.contentJustifyType}
                alignItems={props.alertBoxStyle?.alignmentType}
                spacing={props.alertBoxStyle?.spacing}
                direction={props.alertBoxStyle?.distributionDirection as any}
              >
                <Grid
                  item
                  sx={{
                    width: props.alertBoxStyle?.primaryActionButtonStyle.width,
                  }}
                  order={{
                    xs: props.alertBoxStyle?.primaryActionButtonStyle
                      .buttonOrder,
                  }}
                >
                  <Button
                    sx={{
                      ...props.alertBoxStyle?.primaryActionButtonStyle,
                      p: "1rem",
                      width:
                        props.alertBoxStyle?.primaryActionButtonStyle.width,
                    }}
                    onClick={props.primaryButtonActionHandler}
                  >
                    {props.primaryButtonText}
                  </Button>
                </Grid>
                {props.secondaryButtonText && (
                  <Grid item>
                    <Button
                      sx={{
                        ...secondaryActionButtonStyle,
                        p: "1rem",
                      }}
                      onClick={
                        props.secondaryButtonActionHandler
                          ? props.secondaryButtonActionHandler
                          : props.closeButtonActionHandler
                      }
                    >
                      {props.secondaryButtonText}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
